import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mic, Square, Play, RotateCcw, Check, ChevronRight } from 'lucide-react';
import { buildTable, TABLE_THEMES } from '../lib/tabuada';
import { saveClip, getClipURL, recordingSupported } from '../lib/voiceRecordings';
import { Mascot } from '../components/game/Mascot';
import { unlockAudio, playTap } from '../lib/sound';

export default function RecordVoiceScreen({ table, name, onDone, onBack }) {
  const rows = buildTable(table);
  const th = TABLE_THEMES[table] || TABLE_THEMES[1];
  const [idx, setIdx] = useState(0); // 0..9
  const [recording, setRecording] = useState(false);
  const [clipUrl, setClipUrl] = useState(null);
  const [error, setError] = useState('');
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const urlRef = useRef(null);

  const q = rows[idx];
  const line = idx + 1;
  const supported = recordingSupported();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await getClipURL(table, line);
      if (!cancelled) {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = u;
        setClipUrl(u);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(
    () => () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  const startRec = async () => {
    unlockAudio();
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        await saveClip(table, line, blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(blob);
        setClipUrl(urlRef.current);
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      setError('Não consegui usar o microfone. Verifique a permissão do navegador. 🎤');
    }
  };

  const stopRec = () => {
    playTap();
    if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop();
    setRecording(false);
  };

  const playReview = () => {
    if (clipUrl) {
      const a = new Audio(clipUrl);
      a.play().catch(() => {});
    }
  };

  const next = () => {
    playTap();
    if (idx + 1 >= rows.length) onDone();
    else setIdx((i) => i + 1);
  };

  if (!supported) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-lg flex-col items-center justify-center px-6 text-center">
        <Mascot mood="sad" size={120} />
        <p className="mt-3 font-fred text-xl font-bold text-indigo-900">
          Este navegador não permite gravar áudio 😕
        </p>
        <p className="mt-1 text-sm font-bold text-slate-500">
          Tente pelo Chrome no celular ou computador.
        </p>
        <button
          data-testid="record-back-btn"
          onClick={onBack}
          className="btn-3d mt-5 bg-white px-5 py-3 text-purple-700"
          style={{ borderColor: '#e2e8f0' }}
        >
          <ArrowLeft className="h-5 w-5" /> Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-lg flex-col px-4 py-5">
      <div className="mb-3 flex items-center justify-between">
        <button
          data-testid="record-back-btn"
          onClick={onBack}
          className="btn-3d bg-white px-3 py-2 text-purple-700"
          style={{ borderColor: '#e2e8f0' }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-fred text-xl font-bold text-indigo-900 sm:text-2xl">
          Gravar minha voz 🎙️
        </h1>
        <div className="w-11" />
      </div>

      <p className="mb-1 text-center text-sm font-black text-purple-600" data-testid="record-progress">
        Linha {line} de {rows.length}
      </p>
      <div className="mb-4 h-3 w-full overflow-hidden rounded-full bg-white/70">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${(line / rows.length) * 100}%`, background: th.bg }}
        />
      </div>

      <div className="card-soft flex flex-1 flex-col items-center justify-center p-6 text-center">
        <Mascot mood={recording ? 'talk' : 'happy'} size={100} />
        <p className="mt-2 font-fred text-base font-bold text-slate-500">
          {name ? `${name}, leia em voz alta:` : 'Leia em voz alta:'}
        </p>
        <p className="my-3 font-fred text-3xl font-black text-slate-800 sm:text-4xl">
          {q.a} <span className="text-purple-400">×</span> {q.b}{' '}
          <span className="text-purple-400">=</span>{' '}
          <span style={{ color: th.border }}>{q.result}</span>
        </p>
        <p className="mb-5 text-sm font-bold text-slate-400">
          "{q.a} vezes {q.b} é igual a {q.result}"
        </p>

        {!recording ? (
          <button
            data-testid="record-start-btn"
            onClick={startRec}
            className="btn-3d h-24 w-24 rounded-full bg-rose-500 p-0 text-white"
            style={{ borderColor: '#be123c' }}
          >
            <Mic className="h-10 w-10" />
          </button>
        ) : (
          <motion.button
            data-testid="record-stop-btn"
            onClick={stopRec}
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            className="btn-3d h-24 w-24 rounded-full bg-rose-600 p-0 text-white"
            style={{ borderColor: '#9f1239' }}
          >
            <Square className="h-9 w-9" />
          </motion.button>
        )}
        <p className="mt-2 text-xs font-black text-slate-400">
          {recording ? 'Gravando... toque para parar' : clipUrl ? 'Gravado! 🎉' : 'Toque para gravar'}
        </p>

        {error && <p className="mt-2 text-sm font-bold text-rose-500">{error}</p>}

        {clipUrl && !recording && (
          <div className="mt-4 flex items-center gap-3">
            <button
              data-testid="record-play-btn"
              onClick={playReview}
              className="btn-3d bg-sky-400 px-4 py-2 text-sm text-white"
              style={{ borderColor: '#0284C7' }}
            >
              <Play className="h-4 w-4" /> Ouvir
            </button>
            <button
              data-testid="record-redo-btn"
              onClick={startRec}
              className="btn-3d bg-white px-4 py-2 text-sm text-purple-700"
              style={{ borderColor: '#e2e8f0' }}
            >
              <RotateCcw className="h-4 w-4" /> Regravar
            </button>
          </div>
        )}
      </div>

      <button
        data-testid="record-next-btn"
        onClick={next}
        disabled={recording}
        className="btn-3d mt-4 w-full px-6 py-4 text-lg text-white"
        style={{ background: idx + 1 >= rows.length ? '#10B981' : th.bg, borderColor: idx + 1 >= rows.length ? '#059669' : th.border }}
      >
        {idx + 1 >= rows.length ? (
          <>
            <Check className="h-6 w-6" /> Concluir
          </>
        ) : (
          <>
            Próxima linha <ChevronRight className="h-6 w-6" />
          </>
        )}
      </button>
    </div>
  );
}
